import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { Menu, Clock, CirclePlus, Download, ChevronDown, FileText, FileSpreadsheet, Star } from 'lucide-react';
import { getSelectedUnit } from '../../storage/Storage';
import { searchSalonParent, deleteSalonParent, searchSalonChild, deleteSalonChild, addBestService } from '../../constant/Constant';
import { HitApi } from '../../Api/ApiHit';
import PageHeader from '../../PageHeader/PageHeader';
import EditButton from '../../components/Edit/EditButton';
import DeleteButton from '../../components/Delete/DeleteButton';
import toast from 'react-hot-toast';
import { hasPermission } from '../../utils/permissionUtils';
import DeleteConfirmationModal from '../../components/CustomModal/DeleteConfirmationModal';
import { exportToCSV, exportToPDF } from '../../utils/exportUtils';

function SalonService() {
    const navigate = useNavigate();

    const [salonParents, setSalonParents] = useState([]);
    const [selectedParent, setSelectedParent] = useState(null);
    const [childService, setChildService] = useState(null);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deletingRow, setDeletingRow] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteType, setDeleteType] = useState('parent'); // 'parent' or 'child'

    // Export dropdown state
    const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
    const exportDropdownRef = useRef(null);

    const canEditService = hasPermission('services', 'write');
    const canDeleteService = hasPermission('services', 'delete');
    const canAddService = hasPermission('services', 'write');

    useEffect(() => {
        fetchSalonParents();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
                setIsExportDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchSalonParents = () => {
        const json = {
            page: 1,
            limit: 100000,
            search: {
                unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
            }
        };

        HitApi(json, searchSalonParent).then((res) => {
            if (res?.data?.docs) {
                setSalonParents(res.data.docs);
            } else {
                setSalonParents([]);
                toast.error('Failed to fetch salon groups. Please try again.');
            }
        }).catch((err) => {
            setSalonParents([]);
            toast.error('Error fetching salon groups. Please try again.');
            console.error(err);
        });
    };

    const showSelectedChild = (parent) => {
        setSelectedParent(parent);

        const json = {
            page: 1,
            limit: 100000,
            search: { parentId: parent?._id }
        };

        HitApi(json, searchSalonChild).then((res) => {
            if (res?.data?.docs) {
                setChildService(res.data.docs);
            } else {
                setChildService([]);
                toast.error('Failed to fetch salon child services. Please try again.');
            }
        }).catch((err) => {
            setChildService([]);
            toast.error('Error fetching salon child services. Please try again.');
            console.error(err);
        });
    };

    const handleEdit = (row) => {
        if (row?._id) navigate(`/salonParent/edit/${row._id}`);
        else toast.error('Unable to edit: Salon group ID not found');
    };

    const handleDelete = (row) => {
        setDeletingRow(row);
        setDeleteType('parent');
        setIsDeleteModalOpen(true);
    };

    const handleEditChild = (row) => {
        if (row?._id) navigate(`/editSalonChild/${row._id}`);
        else toast.error('Unable to edit: Salon child ID not found');
    };

    const handleDeleteChild = (row) => {
        setDeletingRow(row);
        setDeleteType('child');
        setIsDeleteModalOpen(true);
    };

    const handleAddToBestService = (child) => {
        const payload = {
            name: child.name,
            description: child.childDesc || '',
            price: child.price || 0,
            member_price: child.member_price || 0,
            incentive: child.incentive || 0,
            gender: child.gender || '',
            img: child.img || '',
            staffIds: child.staffIds || [],
            unitIds: getSelectedUnit()?._id,
            products: child.products || [],
            miscItems: child.miscItems || [],
            service_time: child.service_time || '0',
            serviceMargin: child.serviceMargin || 0,
            salonChildId: child._id
        };

        HitApi(payload, addBestService).then((res) => {
            if (res?.statusCode === 201 || res?.statusCode === 200 || res?.success) {
                toast.success('Service added to Best Services!');
            } else {
                toast.error(res?.message || 'Failed to add to best services');
            }
        }).catch((err) => {
            toast.error('Error adding to best services. Please try again.');
            console.error(err);
        });
    };

    const confirmDelete = () => {
        setIsDeleting(true);
        const json = { _id: deletingRow?._id };

        if (deleteType === 'child') {
            HitApi(json, deleteSalonChild).then((res) => {
                if (res?.success || res?.message?.includes('deleted successfully')) {
                    toast.success('Salon child service deleted successfully!');
                    showSelectedChild(selectedParent);
                } else {
                    toast.error(res?.message || 'Failed to delete salon child service');
                }
            }).catch((err) => {
                toast.error('Error occurred while deleting salon child service. Please try again.');
                console.error(err);
            }).finally(() => {
                setIsDeleting(false);
                setIsDeleteModalOpen(false);
                setDeletingRow(null);
            });
        } else {
            HitApi(json, deleteSalonParent).then((res) => {
                if (res?.success || res?.message?.includes('deleted successfully')) {
                    toast.success('Salon group deleted successfully!');
                    fetchSalonParents();

                    if (selectedParent?._id === deletingRow?._id) {
                        setChildService(null);
                        setSelectedParent(null);
                    }
                } else {
                    toast.error(res?.message || 'Failed to delete salon group');
                }
            }).catch((err) => {
                toast.error('Error occurred while deleting salon group. Please try again.');
                console.error(err);
            }).finally(() => {
                setIsDeleting(false);
                setIsDeleteModalOpen(false);
                setDeletingRow(null);
            });
        }
    };

    const getRandomColor = (index) => {
        const colors = [
            'border-l-blue-500', 'border-l-green-500', 'border-l-yellow-500',
            'border-l-red-500', 'border-l-purple-500', 'border-l-pink-500',
            'border-l-indigo-500', 'border-l-teal-500'
        ];
        return colors[index % colors.length];
    };

    const formatServiceTime = (serviceTime) => {
        if (!serviceTime) return '00:00';
        return serviceTime;
    };

    // Export All Salon Services (Parents with their Children)
    const handleExportAllServices = async (format) => {
        setIsExportDropdownOpen(false);

        try {
            toast.loading('Preparing export...');

            // Fetch all salon parent services
            const parentJson = {
                page: 1,
                limit: 100000,
                search: {
                    unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
                }
            };

            const parentRes = await HitApi(parentJson, searchSalonParent);

            if (!parentRes?.data?.docs || parentRes.data.docs.length === 0) {
                toast.dismiss();
                toast.error('No salon groups to export');
                return;
            }

            const allParents = parentRes.data.docs;
            const exportData = [];
            let serialNumber = 1;

            // Fetch children for each parent
            for (const parent of allParents) {
                const childJson = {
                    page: 1,
                    limit: 100000,
                    search: { parentId: parent._id }
                };

                const childRes = await HitApi(childJson, searchSalonChild);
                const children = childRes?.data?.docs || [];

                if (children.length > 0) {
                    // Add each child with parent info
                    children.forEach((child) => {
                        exportData.push({
                            serialNumber: serialNumber++,
                            groupName: parent.name || 'N/A',
                            serviceName: child.name || 'N/A',
                            price: child.price ? `Rs. ${Number(child.price).toFixed(2)}` : 'Rs. 0.00',
                            memberPrice: child.member_price ? `Rs. ${Number(child.member_price).toFixed(2)}` : 'Rs. 0.00',
                            duration: formatServiceTime(child.service_time),
                            status: child.status || 'Active',
                            createdAt: child.createdAt ? new Date(child.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short', day: 'numeric'
                            }) : 'N/A'
                        });
                    });
                } else {
                    // Add parent with no services
                    exportData.push({
                        serialNumber: serialNumber++,
                        groupName: parent.name || 'N/A',
                        serviceName: 'No services',
                        price: '-',
                        memberPrice: '-',
                        duration: '-',
                        status: parent.status || 'Active',
                        createdAt: parent.createdAt ? new Date(parent.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric'
                        }) : 'N/A'
                    });
                }
            }

            if (exportData.length === 0) {
                toast.dismiss();
                toast.error('No data to export');
                return;
            }

            const exportHeaders = [
                { title: 'S.No', key: 'serialNumber' },
                { title: 'Group Name', key: 'groupName' },
                { title: 'Service Name', key: 'serviceName' },
                { title: 'Price', key: 'price' },
                { title: 'Member Price', key: 'memberPrice' },
                { title: 'Duration', key: 'duration' },
                { title: 'Status', key: 'status' },
                { title: 'Created At', key: 'createdAt' }
            ];

            const filename = `salon_services_export_${new Date().toISOString().split('T')[0]}`;

            toast.dismiss();

            if (format === 'csv') {
                exportToCSV(exportData, exportHeaders, `${filename}.csv`);
                toast.success(`Exported ${exportData.length} salon services to CSV`);
            } else if (format === 'pdf') {
                exportToPDF(exportData, exportHeaders, `${filename}.pdf`, 'Salon Services Report');
                toast.success(`Exported ${exportData.length} salon services to PDF`);
            }
        } catch (error) {
            toast.dismiss();
            toast.error('Error exporting data');
            console.error('Export error:', error);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-5">
            <div className="flex items-center justify-between mb-4">
                <PageHeader title={'Salon Service'} description={'Salon Service Management'} />

                {/* Export Data Dropdown */}
                <div className="relative" ref={exportDropdownRef}>
                    <button
                        onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-gray-700 font-medium"
                    >
                        <Download size={16} />
                        Export Data
                        <ChevronDown size={16} className={`transition-transform ${isExportDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isExportDropdownOpen && (
                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                            <button
                                onClick={() => handleExportAllServices('csv')}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 text-gray-700"
                            >
                                <FileSpreadsheet size={18} className="text-green-600" />
                                Export as CSV
                            </button>
                            <button
                                onClick={() => handleExportAllServices('pdf')}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 text-gray-700 border-t border-gray-100"
                            >
                                <FileText size={18} className="text-red-600" />
                                Export as PDF
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex">
                {/* Sidebar */}
                <div className="w-80 bg-white border-r min-h-screen">
                    <div className="p-4 border-b bg-gray-50">
                        {canAddService && (
                            <NavLink to="/addSalonParent">
                                <button className="w-full flex items-center justify-center gap-2 bg-black text-white px-4 py-2 rounded-lg">
                                    <CirclePlus size={16} />
                                    Add Group
                                </button>
                            </NavLink>
                        )}
                    </div>

                    <div className="p-2">
                        {salonParents.length > 0 ? (
                            salonParents.map((parent) => (
                                <div
                                    key={parent._id}
                                    className={`group flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${selectedParent?._id === parent._id
                                        ? 'bg-blue-50 border-l-4 border-black'
                                        : 'hover:bg-gray-50'
                                        }`}
                                    onClick={() => showSelectedChild(parent)}
                                >
                                    <div className="flex items-center gap-3">
                                        <Menu size={16} className="text-gray-400" />
                                        <span className="font-medium text-gray-900">{parent.name}</span>
                                    </div>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                        {canEditService && (
                                            <EditButton onEdit={() => handleEdit(parent)} itemName="Salon group" />
                                        )}
                                        {canDeleteService && (
                                            <DeleteButton onDelete={() => handleDelete(parent)} itemName="Salon group" />
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-500">No salon groups found</div>
                        )}
                    </div>
                </div>

                {/* Right Panel */}
                <div className="flex-1 bg-white">
                    {childService ? (
                        <div>
                            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    {selectedParent?.name} Services
                                </h2>
                                {canAddService && (
                                    <NavLink to={`/addSalonChild/${selectedParent?._id}`}>
                                        <button className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg">
                                            <CirclePlus size={16} />
                                            Add Service
                                        </button>
                                    </NavLink>
                                )}
                            </div>

                            <div className="p-6 space-y-2">
                                {childService.length > 0 ? (
                                    childService.map((child, index) => (
                                        <div
                                            key={child._id}
                                            className={`group flex items-center justify-between p-4 rounded-lg border-l-4 ${getRandomColor(index)} bg-gray-50 hover:bg-gray-100`}
                                        >
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <h3 className="font-medium text-gray-900">{child.name}</h3>
                                                    <div className="flex items-center gap-4">
                                                        <div className="text-right">
                                                            <div className="font-semibold text-gray-900">
                                                                ₹{child.price?.toLocaleString() || '0'}
                                                            </div>
                                                            {child.member_price && (
                                                                <div className="text-sm text-gray-500">
                                                                    Member: ₹{child.member_price.toLocaleString()}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                                            <Clock size={14} />
                                                            <span>{formatServiceTime(child.service_time)}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                                            <button
                                                                onClick={() => handleAddToBestService(child)}
                                                                className="p-2 rounded-lg bg-yellow-100 hover:bg-yellow-200 transition-colors"
                                                                title="Add to Best Services"
                                                            >
                                                                <Star size={16} className="text-yellow-600" />
                                                            </button>
                                                            {canEditService && (
                                                                <EditButton onEdit={() => handleEditChild(child)} itemName="Salon child" />
                                                            )}
                                                            {canDeleteService && (
                                                                <DeleteButton onDelete={() => handleDeleteChild(child)} itemName="Salon child" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-8 text-gray-600">No services found in this category</div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full text-center text-gray-600">
                            Select a salon group to view services
                        </div>
                    )}
                </div>
            </div>

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setDeletingRow(null);
                }}
                onConfirm={confirmDelete}
                itemName={deletingRow?.name}
                itemType={deleteType === 'child' ? 'salon child service' : 'salon group'}
                isLoading={isDeleting}
                warningMessage={deleteType === 'child' ? 'This will delete this salon child service.' : 'This will also delete all child services related to this salon group.'}
            />
        </div>
    );
}

export default SalonService;
